import axios from 'axios';
import moment from 'moment';
import { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { IActivityType, IAddress, ILine } from '../../../../../types';
import 'moment/locale/fr';

interface ILinesProps {
  id: string;
  expenseType: 'expense' | 'trip';
  line?: ILine;
  show: boolean;
  onClose: () => void;
}

const MANUAL_ADDRESS = '__manual__';

const Lines = ({ id, expenseType, line, show = false, onClose = () => {} }: ILinesProps) => {
  const [addresses, setAddresses] = useState<IAddress[]>([]);
  const [activityTypes, setActivityTypes] = useState<IActivityType[]>([]);
  const [date, setDate] = useState('');
  const [originPreset, setOriginPreset] = useState(MANUAL_ADDRESS);
  const [destinationPreset, setDestinationPreset] = useState(MANUAL_ADDRESS);
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [activityType, setActivityType] = useState('');
  const [activityName, setActivityName] = useState('');
  const [direction, setDirection] = useState<'A' | 'R' | 'AR'>('A');
  const [description, setDescription] = useState('');
  const [distance, setDistance] = useState(0);

  const initModal = (availableAddresses = addresses, availableTypes = activityTypes) => {
    const findPreset = (value: string, resolvedAddress?: string) => {
      const preset = availableAddresses.find(
        (item) => item.name === value || (!!resolvedAddress && item.address === resolvedAddress)
      );
      return preset;
    };

    if (line) {
      const originAddress = findPreset(line.origin, line.origin_address);
      const destinationAddress = findPreset(line.destination, line.destination_address);
      setDate(moment(line.date).format('YYYY-MM-DD'));
      setOriginPreset(originAddress?._id ?? MANUAL_ADDRESS);
      setDestinationPreset(destinationAddress?._id ?? MANUAL_ADDRESS);
      setOrigin(originAddress ? originAddress.address : line.origin_address ?? line.origin);
      setDestination(
        destinationAddress ? destinationAddress.address : line.destination_address ?? line.destination
      );
      setDistance(line.distance);

      if (line.activity_type || line.activity_name || line.direction) {
        setActivityType(line.activity_type ?? '');
        setActivityName(line.activity_name ?? '');
        setDirection(line.direction ?? 'A');
      } else {
        const separator = line.description.lastIndexOf(' - ');
        const parsedDirection = line.description.slice(separator + 3);
        const activity = separator > 0 ? line.description.slice(0, separator) : line.description;
        const parsedType = availableTypes.find((item) => activity.startsWith(`${item.name} - `));
        setActivityType(parsedType?.name ?? '');
        setActivityName(parsedType ? activity.slice(parsedType.name.length + 3) : activity);
        setDirection(parsedDirection === 'AR' ? 'AR' : parsedDirection === 'R' ? 'R' : 'A');
      }
      setDescription(line.description);
    } else {
      setDate('');
      setOriginPreset(MANUAL_ADDRESS);
      setDestinationPreset(MANUAL_ADDRESS);
      setOrigin('');
      setDestination('');
      setActivityType('');
      setActivityName('');
      setDirection('A');
      setDescription('');
      setDistance(0);
    }
  };

  useEffect(() => {
    if (!show) return;

    if (expenseType === 'trip') {
      Promise.all([
        axios.get<IAddress[]>('/api/address'),
        axios.get<IActivityType[]>('/api/activity-type'),
      ])
        .then(([addressResponse, typeResponse]) => {
          setAddresses(addressResponse.data);
          setActivityTypes(typeResponse.data);
          initModal(addressResponse.data, typeResponse.data);
        })
        .catch(() => toast.error('Unable to load trip settings'));
    } else {
      initModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, line, expenseType]);

  const getAddress = (preset: string, manualValue: string) => {
    const selected = addresses.find((item) => item._id === preset);
    return selected ? { name: selected.name, address: selected.address } : { name: manualValue, address: manualValue };
  };

  const handlePresetChange = (preset: string, originField: boolean) => {
    const selected = addresses.find((item) => item._id === preset);
    if (originField) {
      setOriginPreset(preset);
      if (selected) setOrigin(selected.address);
    } else {
      setDestinationPreset(preset);
      if (selected) setDestination(selected.address);
    }
  };

  const handleCancel = () => {
    initModal();
    onClose();
  };

  const handleSave = () => {
    if (expenseType === 'trip' && (!activityType || !activityName.trim())) {
      toast.error('Select an activity type and enter an activity name');
      return;
    }

    const originData = getAddress(originPreset, origin);
    const destinationData = getAddress(destinationPreset, destination);
    const lineData = {
      date,
      origin: originData.name,
      destination: destinationData.name,
      description:
        expenseType === 'trip'
          ? `${activityType} - ${activityName.trim()} - ${direction}`
          : description,
      distance,
      ...(expenseType === 'trip' && {
        ...(originPreset !== MANUAL_ADDRESS && { origin_address: originData.address }),
        ...(destinationPreset !== MANUAL_ADDRESS && { destination_address: destinationData.address }),
      }),
      ...(expenseType === 'trip' && {
        activity_type: activityType,
        activity_name: activityName.trim(),
        direction,
      }),
    };

    const request = line?._id
      ? axios.put(`/api/expense/${id}/line/${line._id}`, lineData)
      : axios.post(`/api/expense/${id}/line`, lineData);

    request.then(handleCancel).catch((error) => toast.error(error.response?.data ?? 'Unable to save line'));
  };

  const addressPicker = (
    label: string,
    preset: string,
    value: string,
    originField: boolean
  ) => (
    <Form.Group className="mb-2">
      <Form.Label>{label}</Form.Label>
      {expenseType === 'trip' && (
        <Form.Select value={preset} onChange={(event) => handlePresetChange(event.target.value, originField)}>
          <option value={MANUAL_ADDRESS}>Manual address</option>
          {addresses.map((item) => (
            <option key={item._id} value={item._id}>{item.name}</option>
          ))}
        </Form.Select>
      )}
      <Form.Control
        className={expenseType === 'trip' ? 'mt-1' : ''}
        value={value}
        onChange={(event) => (originField ? setOrigin(event.target.value) : setDestination(event.target.value))}
        placeholder="Address"
        readOnly={expenseType === 'trip' && preset !== MANUAL_ADDRESS}
        required
      />
    </Form.Group>
  );

  return (
    <Modal show={show} onHide={handleCancel}>
      <Modal.Header closeButton>
        <Modal.Title>{line ? 'Edit line' : 'New line'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-2">
            <Form.Label>Date</Form.Label>
            <Form.Control type="date" value={date} onChange={(event) => setDate(event.target.value)} required />
          </Form.Group>
          {addressPicker('Origin', originPreset, origin, true)}
          {addressPicker('Destination', destinationPreset, destination, false)}
          {expenseType === 'trip' ? (
            <>
              <Form.Group className="mb-2">
                <Form.Label>Activity type</Form.Label>
                <Form.Select value={activityType} onChange={(event) => setActivityType(event.target.value)} required>
                  <option value="">Select an activity type</option>
                  {activityTypes.map((item) => <option key={item._id} value={item.name}>{item.name}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Activity name</Form.Label>
                <Form.Control value={activityName} onChange={(event) => setActivityName(event.target.value)} required />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Direction</Form.Label>
                <Form.Select
                  value={direction}
                  onChange={(event) => setDirection(event.target.value as 'A' | 'R' | 'AR')}
                >
                  <option value="A">A - Aller</option>
                  <option value="R">R - Retour</option>
                  <option value="AR">AR - Aller / Retour</option>
                </Form.Select>
              </Form.Group>
            </>
          ) : (
            <Form.Group className="mb-2">
              <Form.Label>Description</Form.Label>
              <Form.Control value={description} onChange={(event) => setDescription(event.target.value)} required />
            </Form.Group>
          )}
          <Form.Group>
            <Form.Label>Distance (km)</Form.Label>
            <Form.Control type="number" min="0" step="0.1" value={distance} onChange={(event) => setDistance(Number(event.target.value))} required />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel}>Close</Button>
        <Button variant="primary" onClick={handleSave}>Save</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default Lines;
